import { useGetSeatingEntitlementQuery } from './useHasSeatingPlus.graphql.generated';

type UseHasSeatingPlusResult = Readonly<{
  hasSeatingPlus: boolean;
  loading: boolean;
}>;

type UseHasSeatingPlusOptions = Readonly<{
  /** Skip the entitlement query entirely (e.g. events opted out of the Guest List vertical). */
  skip?: boolean;
}>;

/**
 * Shared Seating Chart entitlement gate.
 *
 * Returns whether the event holds the paid Seating Chart (Plus) tier, resolved
 * server-side by seat_service (stored tier written by card_service at purchase,
 * plus interim superadmin flag grants). This is the same fact the seating-chart
 * MFE enforces on reads/writes via useSeatingMode, so entry points and
 * enforcement can't disagree: a collaborator on a paid event sees the feature
 * regardless of which Amplitude rollout bucket their own user lands in
 * (SEAT-162 follow-up — the menu was gated on seatingChartEnabled alone, a
 * user-bucketed flag, which hid a paid feature from paying events).
 *
 * `cache-and-network` matches the MFE's read so the menu self-heals right after
 * a purchase instead of waiting for a hard reload.
 */
export const useHasSeatingPlus = (eventId: string, { skip = false }: UseHasSeatingPlusOptions = {}): UseHasSeatingPlusResult => {
  const { data, loading } = useGetSeatingEntitlementQuery({
    variables: { eventId },
    skip: skip || !eventId,
    batchMode: 'fast',
    fetchPolicy: 'cache-and-network',
  });

  return {
    // Fails closed like the MFE: an unreadable entitlement never grants entry.
    hasSeatingPlus: data?.seatingEntitlement.hasSeatingPlus ?? false,
    loading,
  };
};
